import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '../context/AuthContext';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMobileDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleMobileNavClick = (path: string) => {
    navigate(path);
    setMobileDrawerOpen(false);
  };

  const handleLogout = () => {
    logout();
    window.localStorage.removeItem('strapiToken');
    window.localStorage.removeItem('strapiUser');
    navigate('/login');
    handleMenuClose();
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <AppBar
      position="static"
      sx={{
        backgroundColor: '#1976d2',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      }}
    >
      <Toolbar
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingY: 1,
          px: { xs: 1, sm: 2, md: 3 },
          minHeight: { xs: 'auto', sm: '64px' },
          flexWrap: 'wrap',
          gap: { xs: 1, sm: 2 },
        }}
      >
        {/* Title */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2, md: 3 },
            flex: { xs: '0 1 auto', sm: 'auto' },
            order: { xs: 1, sm: 1 },
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 'bold',
              fontSize: { xs: '0.95rem', sm: '1.2rem', md: '1.4rem' },
              letterSpacing: { xs: 0.2, md: 0.5 },
              whiteSpace: 'nowrap',
            }}
          >
            OJRKS Mandir Nirmaan
          </Typography>

          {/* Navigation Menu Items - Hidden on mobile */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: { xs: 1, md: 2 } }}>
            <Button
              color="inherit"
              onClick={() => navigate('/')}
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.9rem', md: '0.95rem' },
                fontWeight: 'bold',
                borderBottom: (isActive('/') || isActive('/expense-report')) ? '3px solid white' : 'none',
                paddingBottom: (isActive('/') || isActive('/expense-report')) ? 1.5 : 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderBottom: '3px solid white',
                  paddingBottom: 1.5,
                },
              }}
            >
              Expense Report
            </Button>
            <Button
              color="inherit"
              onClick={() => navigate('/budget-approval')}
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.9rem', md: '0.95rem' },
                fontWeight: 'bold',
                borderBottom: isActive('/budget-approval') ? '3px solid white' : 'none',
                paddingBottom: isActive('/budget-approval') ? 1.5 : 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderBottom: '3px solid white',
                  paddingBottom: 1.5,
                },
              }}
            >
              Budget Approval
            </Button>
            <Button
              color="inherit"
              onClick={() => navigate('/budget-commitment')}
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.9rem', md: '0.95rem' },
                fontWeight: 'bold',
                borderBottom: isActive('/budget-commitment') ? '3px solid white' : 'none',
                paddingBottom: isActive('/budget-commitment') ? 1.5 : 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  borderBottom: '3px solid white',
                  paddingBottom: 1.5,
                },
              }}
            >
              Commitment And Other Sources
            </Button>
          </Box>

          {/* Mobile Hamburger Menu */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleMobileDrawerToggle}
            sx={{ display: { xs: 'flex', sm: 'none' }, ml: 'auto' }}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        {/* User Menu and Logout */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 2 }, order: { xs: 2, sm: 'auto' }, ml: { xs: 'auto', sm: 0 } }}>
          {user && (
            <Typography
              sx={{
                fontSize: { xs: '0.7rem', sm: '0.9rem' },
                fontWeight: 500,
                display: { xs: 'none', sm: 'block' },
              }}
            >
              {user.username}
            </Typography>
          )}
          <Avatar
            onClick={handleMenuOpen}
            sx={{
              cursor: 'pointer',
              backgroundColor: '#dc004e',
              width: { xs: 36, sm: 40 },
              height: { xs: 36, sm: 40 },
              fontSize: { xs: '0.9rem', sm: '1rem' },
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'scale(1.1)',
              },
            }}
          >
            {user?.username ? user.username.charAt(0).toUpperCase() : 'U'}
          </Avatar>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            slotProps={{
              paper: {
                sx: {
                  minWidth: { xs: '200px', sm: '250px' },
                },
              },
            }}
          >
            <MenuItem disabled>
              <PersonIcon sx={{ mr: 1, fontSize: { xs: '1rem', sm: '1.2rem' } }} />
              <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>{user?.username}</Typography>
            </MenuItem>
            <MenuItem divider disabled>
              <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                {user?.email}
              </Typography>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 1, fontSize: { xs: '1rem', sm: '1.2rem' } }} />
              <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>Logout</Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="top"
        open={mobileDrawerOpen}
        onClose={handleMobileDrawerToggle}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            backgroundColor: '#1976d2',
            color: 'white',
            marginTop: '56px',
            maxHeight: '300px',
            overflow: 'auto',
          },
        }}
      >
        <Box
          sx={{
            p: 0,
            minWidth: '100%',
          }}
        >
          <List>
            <ListItem
              disablePadding
              onClick={() => handleMobileNavClick('/')}
              sx={{
                backgroundColor: (isActive('/') || isActive('/expense-report')) ? 'rgba(255,255,255,0.1)' : 'transparent',
                borderLeft: (isActive('/') || isActive('/expense-report')) ? '4px solid white' : '4px solid transparent',
              }}
            >
              <ListItemButton sx={{ py: 1.5 }}>
                <ListItemText
                  primary="Expense Report"
                  primaryTypographyProps={{
                    sx: { fontSize: { xs: '0.875rem', md: '1rem' }, fontWeight: 'bold' },
                  }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem
              disablePadding
              onClick={() => handleMobileNavClick('/budget-approval')}
              sx={{
                backgroundColor: isActive('/budget-approval') ? 'rgba(255,255,255,0.1)' : 'transparent',
                borderLeft: isActive('/budget-approval') ? '4px solid white' : '4px solid transparent',
              }}
            >
              <ListItemButton sx={{ py: 1.5 }}>
                <ListItemText
                  primary="Budget Approval"
                  primaryTypographyProps={{
                    sx: { fontSize: { xs: '0.875rem', md: '1rem' }, fontWeight: 'bold' },
                  }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem
              disablePadding
              onClick={() => handleMobileNavClick('/budget-commitment')}
              sx={{
                backgroundColor: isActive('/budget-commitment') ? 'rgba(255,255,255,0.1)' : 'transparent',
                borderLeft: isActive('/budget-commitment') ? '4px solid white' : '4px solid transparent',
              }}
            >
              <ListItemButton sx={{ py: 1.5 }}>
                <ListItemText
                  primary="Budget Commitment"
                  primaryTypographyProps={{
                    sx: { fontSize: { xs: '0.875rem', md: '1rem' }, fontWeight: 'bold' },
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Header;
